import { z } from 'zod'

const id = z.string().trim().min(1).max(200)
const version = z.number().int().positive()
const text = z.string().trim().min(1).max(1000)
const quantity = z.string().regex(/^\d{1,15}(\.\d{1,3})?$/)
const positive = quantity.refine((value) => /[1-9]/.test(value))
const day = z.iso.date()
export const stageReferenceSchema = z
  .object({ productionOrderId: id, stageKey: id, version })
  .strict()
export const stagePlanSchema = stageReferenceSchema
  .extend({
    assigneeId: id.nullable().optional(),
    equipmentId: id.nullable().optional(),
    scheduledDate: day.nullable().optional(),
  })
  .refine((value) => ['assigneeId', 'equipmentId', 'scheduledDate'].some((key) => key in value))
/** A completion photo is compressed on the device; the API rejects anything larger. */
export const STAGE_PHOTO_MAX_BYTES = 2 * 1024 * 1024
export const STAGE_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const
export const stageAssignSchema = stageReferenceSchema.extend({ assigneeId: id })
export const stagePauseSchema = stageReferenceSchema.extend({ reason: text })
export const stageOutputSchema = stageReferenceSchema
  .extend({
    good: quantity,
    scrap: quantity,
    reason: text.nullable(),
    note: text.nullable(),
  })
  .refine((value) => /[1-9]/.test(value.good + value.scrap))
  .refine((value) => !/[1-9]/.test(value.scrap) || value.reason !== null)
export const productionPrioritySchema = z
  .object({
    id,
    version,
    priority: z.enum(['low', 'medium', 'high']),
  })
  .strict()
export const planRowCreateSchema = z
  .object({
    productName: text,
    quantity: positive,
    unit: z.string().trim().min(1).max(30),
    amount: quantity,
    sellerEntityId: id,
    customer: text,
    deadline: day,
    note: text.nullable(),
  })
  .strict()
export const planRowArchiveSchema = z.object({ id, version }).strict()
export const equipmentRepairSchema = z.object({ id, version, reason: text.nullable() }).strict()
/** BySoft «Список заданий» PDF, uploaded as the proof of a laser cutting job. */
export const NESTING_REPORT_MAX_BYTES = 20 * 1024 * 1024
export const nestingConfirmSchema = z
  .object({
    matches: z
      .array(
        z
          .object({
            partNumber: z.number().int().positive(),
            productionOrderId: id,
            stageKey: id,
            version,
            quantity: positive,
          })
          .strict(),
      )
      .max(500),
    materialId: id,
    unit: z.string().trim().min(1).max(30),
    quantity: positive,
    equipmentId: id.nullable(),
    workDate: day,
  })
  .strict()
export type NestingConfirm = z.infer<typeof nestingConfirmSchema>
export type StageReference = z.infer<typeof stageReferenceSchema>
export type StagePlan = z.infer<typeof stagePlanSchema>
export type StageAssign = z.infer<typeof stageAssignSchema>
export type StagePause = z.infer<typeof stagePauseSchema>
export type StageOutput = z.infer<typeof stageOutputSchema>
export type ProductionPriorityInput = z.infer<typeof productionPrioritySchema>
export type PlanRowCreate = z.infer<typeof planRowCreateSchema>
export type PlanRowArchive = z.infer<typeof planRowArchiveSchema>
export type EquipmentRepairInput = z.infer<typeof equipmentRepairSchema>
